import { QRCodeSVG } from "qrcode.react";
import { FC, useState } from "react";
import toast from "react-hot-toast";
import { Network, networkLabel } from "rujira.js";
import { IconDenom } from "../icons/IconDenom";
import * as Icons from "../icons/Icons";
import { NetworkIcon } from "../icons/NetworkIcon";
import { useTranslation } from "../../i18n";
import { clipAddress } from "../../helpers";

export type AddressSharePopupProps = {
  address: string;
  network: Network;
  onClose?: () => void;
};

export const AddressSharePopup: FC<AddressSharePopupProps> = ({
  address,
  network,
  onClose,
}) => {
  const { t } = useTranslation("common");
  const [sharing, setSharing] = useState(false);

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address);
      toast.success(t("addressCopied"));
    } catch {
      toast.error(t("shareUrlCopyFailed"));
    }
  };

  const shareAddress = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${networkLabel(network)} address`,
          text: address,
        });
      } else {
        await navigator.clipboard.writeText(address);
        toast.success(t("addressCopied"));
      }
      onClose?.();
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError"))
        toast.error(t("shareUrlCopyFailed"));
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="address-share-popup">
      {onClose && (
        <button
          type="button"
          className="transparent address-share-popup__back"
          aria-label={t("back")}
          onClick={onClose}>
          <Icons.ArrowLeft aria-hidden="true" />
        </button>
      )}

      <div className="address-share-popup__network">
        <NetworkIcon network={network} />
        <span>{networkLabel(network)}</span>
      </div>

      <div className="address-share-popup__qr">
        <QRCodeSVG
          value={address}
          level="H"
          className="address-share-popup__qr-code"
        />
        <span className="address-share-popup__qr-badge">
          <IconDenom denom="RUJI" />
        </span>
      </div>

      <div className="address-share-popup__address" title={address}>
        <span>{clipAddress(address)}</span>
        <button
          type="button"
          className="transparent address-share-popup__action"
          aria-label={t("copyAddress")}
          onClick={() => void copyAddress()}>
          <Icons.Copy aria-hidden="true" />
        </button>
        <button
          type="button"
          className="transparent address-share-popup__action"
          aria-label={t("share")}
          disabled={sharing}
          onClick={() => void shareAddress()}>
          <Icons.Share aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};
